import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Briefcase, TrendingUp, AlertTriangle, BarChart3, Calendar, Euro, X, ExternalLink, Construction, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mese');
  const [activeView, setActiveView] = useState('operatori'); // 'operatori', 'progetti', 'fatturato', 'cantieri'
  const [utentiStats, setUtentiStats] = useState([]);
  const [progetti, setProgetti] = useState([]);
  const [ordiniLavoro, setOrdiniLavoro] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [statsFatturato, setStatsFatturato] = useState({
    mensile: { fatturato: 0, daFatturare: 0, ordiniFatturati: [], ordiniDaFatturare: [] },
    trimestrale: { fatturato: 0, daFatturare: 0, ordiniFatturati: [], ordiniDaFatturare: [] },
    annuale: { fatturato: 0, daFatturare: 0, ordiniFatturati: [], ordiniDaFatturare: [] }
  });
  const [statsGenerali, setStatsGenerali] = useState({
    totaleProgetti: 0,
    progettiInCorso: 0,
    progettiCompletati: 0,
    progettiInRitardo: 0,
    progettiInValidazione: 0,
    progettiInRevisione: 0,
    progettiSospesi: 0
  });
  const [modalFatturatoOpen, setModalFatturatoOpen] = useState(false);
  const [modalFatturatoData, setModalFatturatoData] = useState({ tipo: '', periodo: '', ordini: [] });
  const [modalProgettoOpen, setModalProgettoOpen] = useState(false);
  const [progettoDetail, setProgettoDetail] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    fetchData();
  }, [periodo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Calcola date periodo
      const oggi = new Date();
      let dataInizio = new Date();
      
      if (periodo === 'mese') {
        dataInizio.setMonth(dataInizio.getMonth() - 1);
      } else if (periodo === 'trimestre') {
        dataInizio.setMonth(dataInizio.getMonth() - 3);
      } else if (periodo === 'anno') {
        dataInizio.setFullYear(dataInizio.getFullYear() - 1);
      }

      // Fetch utenti, progetti, ordini di lavoro e cantieri
      const [usersRes, progettiRes, ordiniRes, cantieriRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/progetti`, getAuthHeaders()),
        axios.get(`${API}/ordini-lavoro`, getAuthHeaders()),
        axios.get(`${API}/cantieri`, getAuthHeaders())
      ]);

      const utenti = usersRes.data;
      const tuttiProgetti = progettiRes.data;
      const tuttiOrdini = ordiniRes.data;
      const tuttiCantieri = cantieriRes.data;

      // Filtra progetti nel periodo o ancora in corso
      // Mostra progetti che:
      // 1. Sono stati creati nel periodo selezionato, OPPURE
      // 2. Sono ancora "in_corso" (attivi)
      const progettiPeriodo = tuttiProgetti.filter(p => {
        const dataCreazione = new Date(p.created_at);
        const creatoNelPeriodo = dataCreazione >= dataInizio && dataCreazione <= oggi;
        const ancoraAttivo = p.stato === 'in_corso';
        return creatoNelPeriodo || ancoraAttivo;
      });

      setProgetti(progettiPeriodo);
      setCantieri(tuttiCantieri);

      // Calcola stats per utente
      // Include: utenti tecnici (ingegneri, fattibilità) E utenti che sono responsabili di almeno un progetto
      const ambitiTecnici = ['ingegneria', 'ingegnere', 'fattibilita'];
      
      const hasAmbitoTecnico = (u) => {
        // Controlla ambiti (array)
        if (u.ambiti && Array.isArray(u.ambiti)) {
          if (u.ambiti.some(a => ambitiTecnici.includes(a))) return true;
        }
        // Controlla ambito (singolo - legacy)
        if (u.ambito && ambitiTecnici.includes(u.ambito)) return true;
        return false;
      };
      
      // Trova tutti gli ID e nomi degli utenti assegnati ai progetti (referenti + responsabili fattibilità)
      // NOTA: NON include ingegnere responsabile - solo referenti e responsabili fattibilità
      const assegnatiIds = new Set();
      const assegnatiNomi = new Set();
      progettiPeriodo.forEach(p => {
        // Referenti progetto
        if (p.referenti_progetto && Array.isArray(p.referenti_progetto)) {
          p.referenti_progetto.forEach(r => {
            if (r.user_id) assegnatiIds.add(r.user_id);
            if (r.nome) assegnatiNomi.add(r.nome);
            if (r.nome_completo) assegnatiNomi.add(r.nome_completo);
          });
        }
        // Responsabile fattibilità
        if (p.responsabile_fattibilita_id) {
          assegnatiIds.add(p.responsabile_fattibilita_id);
        }
        if (p.responsabile_fattibilita_nome) {
          assegnatiNomi.add(p.responsabile_fattibilita_nome);
        }
      });

      // Filtra utenti: quelli che sono assegnati ad almeno un progetto (come referente o responsabile fattibilità)
      const utentiTecnici = utenti.filter(u => {
        if (assegnatiIds.has(u.id)) return true;
        if (assegnatiNomi.has(u.nome)) return true;
        const nomeCompleto = `${u.nome} ${u.cognome || ''}`.trim();
        if (assegnatiNomi.has(nomeCompleto)) return true;
        return false;
      });
      
      const statsPerUtente = utentiTecnici
        .map(utente => {
          // Cerca progetti assegnati a questo utente (per ID o per nome)
          const nomeCompleto = `${utente.nome} ${utente.cognome || ''}`.trim();
          const progettiUtente = progettiPeriodo.filter(p => {
            // Referenti progetto
            if (p.referenti_progetto && Array.isArray(p.referenti_progetto)) {
              const isReferente = p.referenti_progetto.some(r => 
                r.user_id === utente.id || 
                r.nome === utente.nome || 
                r.nome_completo === utente.nome ||
                r.nome_completo === nomeCompleto
              );
              if (isReferente) return true;
            }
            // Responsabile fattibilità
            if (p.responsabile_fattibilita_id === utente.id) return true;
            if (p.responsabile_fattibilita_nome === utente.nome) return true;
            if (p.responsabile_fattibilita_nome === nomeCompleto) return true;
            
            return false;
          });

          const progettiInCorso = progettiUtente.filter(p => p.stato === 'in_corso');
          const progettiCompletati = progettiUtente.filter(p => p.stato === 'completato');
          const progettiInRitardo = progettiUtente.filter(p => {
            if (p.stato === 'completato') return false;
            // Se in validazione con data fine effettiva, il conteggio è sospeso (non è in ritardo)
            if (p.stato === 'in_validazione' && p.data_fine_effettiva) return false;
            if (!p.data_fine_prevista) return false;
            return new Date(p.data_fine_prevista) < oggi;
          });

          // Determina l'ambito da mostrare (preferisce ambiti array se presente)
          let ambitoDisplay = '';
          if (utente.ambiti && Array.isArray(utente.ambiti) && utente.ambiti.length > 0) {
            ambitoDisplay = utente.ambiti[0]; // Prende il primo ambito
          } else if (utente.ambito) {
            ambitoDisplay = utente.ambito;
          } else if (progettiUtente.length > 0) {
            // Se non ha ambito ma ha progetti, consideralo ingegneria
            ambitoDisplay = 'ingegnere';
          }

          return {
            id: utente.id,
            nome: utente.nome,
            cognome: utente.cognome || '',
            email: utente.email,
            ambito: ambitoDisplay,  // Ambito per distinguere ingegneria/fattibilita
            ambiti: utente.ambiti || [],  // Array completo per riferimento
            punteggio: utente.punteggio_performance || 0,
            totaleProgetti: progettiUtente.length,
            progettiInCorso: progettiInCorso.length,
            progettiCompletati: progettiCompletati.length,
            progettiInRitardo: progettiInRitardo.length,
            oreStandard: progettiUtente.reduce((sum, p) => sum + (p.ore_standard_manuali || p.ore_lavorate_standard || 0), 0),
            oreStraordinario: progettiUtente.reduce((sum, p) => sum + (p.ore_straordinario || 0), 0),
            progetti: progettiUtente
          };
        })
        // Filtra utenti senza ambito e senza progetti
        .filter(u => u.ambito || u.totaleProgetti > 0)
        .sort((a, b) => b.punteggio - a.punteggio);

      setUtentiStats(statsPerUtente);
      setOrdiniLavoro(tuttiOrdini);

      // Stats generali
      setStatsGenerali({
        totaleProgetti: progettiPeriodo.length,
        progettiInCorso: progettiPeriodo.filter(p => p.stato === 'in_corso').length,
        progettiInValidazione: progettiPeriodo.filter(p => p.stato === 'in_validazione').length,
        progettiInRevisione: progettiPeriodo.filter(p => p.stato === 'in_revisione').length,
        progettiCompletati: progettiPeriodo.filter(p => p.stato === 'completato').length,
        progettiSospesi: progettiPeriodo.filter(p => p.stato === 'sospeso').length,
        progettiInRitardo: progettiPeriodo.filter(p => {
          if (p.stato === 'completato') return false;
          // Se in validazione con data fine effettiva, il conteggio è sospeso (non è in ritardo)
          if (p.stato === 'in_validazione' && p.data_fine_effettiva) return false;
          if (!p.data_fine_prevista) return false;
          return new Date(p.data_fine_prevista) < new Date();
        }).length
      });

      // Calcola stats fatturato per 3 periodi
      const calcolaStatsFatturato = (mesi) => {
        const dataLimite = new Date();
        dataLimite.setMonth(dataLimite.getMonth() - mesi);

        const ordiniPeriodo = tuttiOrdini.filter(ordine => {
          const dataCreazione = new Date(ordine.created_at);
          return dataCreazione >= dataLimite;
        });

        let fatturato = 0;
        let daFatturare = 0;
        const ordiniFatturati = [];
        const ordiniDaFatturare = [];

        ordiniPeriodo.forEach(ordine => {
          if (!ordine.sal_list || ordine.sal_list.length === 0) return;

          let fatturatoOrdine = 0;
          let daFatturareOrdine = 0;
          const salFatturati = [];
          const salDaFatturare = [];

          ordine.sal_list.forEach(sal => {
            if (sal.stato === 'fatturato') {
              fatturatoOrdine += sal.importo || 0;
              salFatturati.push(sal);
            } else if (sal.stato === 'da_fatturare') {
              daFatturareOrdine += sal.importo || 0;
              salDaFatturare.push(sal);
            }
          });

          if (fatturatoOrdine > 0) {
            fatturato += fatturatoOrdine;
            ordiniFatturati.push({
              ...ordine,
              importoFatturato: fatturatoOrdine,
              salFatturati
            });
          }

          if (daFatturareOrdine > 0) {
            daFatturare += daFatturareOrdine;
            ordiniDaFatturare.push({
              ...ordine,
              importoDaFatturare: daFatturareOrdine,
              salDaFatturare
            });
          }
        });

        return { fatturato, daFatturare, ordiniFatturati, ordiniDaFatturare };
      };

      setStatsFatturato({
        mensile: calcolaStatsFatturato(1),
        trimestrale: calcolaStatsFatturato(3),
        annuale: calcolaStatsFatturato(12)
      });

    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento dei report');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (percentualeCompletati) => {
    if (percentualeCompletati >= 75) return 'text-green-600';
    if (percentualeCompletati >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRitardoColor = (giorni) => {
    if (giorni > 7) return 'bg-red-100 text-red-700 border-red-300';
    if (giorni > 0) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  const calcolaGiorniRitardo = (dataFinePrevista) => {
    const oggi = new Date();
    const dataFine = new Date(dataFinePrevista);
    const diffTime = oggi - dataFine;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-slate-600">Caricamento report...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="mr-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="hidden sm:block bg-orange-500 p-3 rounded-xl shadow-md">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Report e Statistiche</h1>
                <p className="text-sm text-slate-600">Dashboard monitoraggio operatori e progetti</p>
              </div>
            </div>

            {/* Filtro Periodo */}
            <div className="flex flex-wrap items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-500" />
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger className="w-40 sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mese">📅 Ultimo Mese</SelectItem>
                  <SelectItem value="trimestre">📆 Ultimo Trimestre</SelectItem>
                  <SelectItem value="anno">🗓️ Ultimo Anno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8 space-y-6">
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-2 flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveView('operatori')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeView === 'operatori'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Users className="w-5 h-5" />
            Performance Operatori
          </button>
          <button
            onClick={() => setActiveView('progetti')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeView === 'progetti'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Briefcase className="w-5 h-5" />
            Stato Progetti
          </button>
          <button
            onClick={() => setActiveView('fatturato')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeView === 'fatturato'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Euro className="w-5 h-5" />
            Fatturato
          </button>
          <button
            onClick={() => navigate('/report-ore')}
            className="flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 bg-cyan-600 text-white hover:bg-cyan-700 shadow-lg"
            data-testid="report-ore-btn"
          >
            <Clock className="w-5 h-5" />
            Ore Lavorate
          </button>
          <button
            onClick={() => setActiveView('cantieri')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              activeView === 'cantieri'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Construction className="w-5 h-5" />
            Cantieri
          </button>
        </div>

        {/* Stats Cards - Nasconde per vista Cantieri */}
        {activeView !== 'cantieri' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Totale Progetti</p>
                <p className="text-3xl font-bold text-slate-900">{statsGenerali.totaleProgetti}</p>
              </div>
              <Briefcase className="w-10 h-10 text-slate-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-blue-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">In Corso</p>
                <p className="text-3xl font-bold text-blue-600">{statsGenerali.progettiInCorso}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-green-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Completati</p>
                <p className="text-3xl font-bold text-green-600">{statsGenerali.progettiCompletati}</p>
              </div>
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md border border-red-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">In Ritardo</p>
                <p className="text-3xl font-bold text-red-600">{statsGenerali.progettiInRitardo}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </div>
        </div>
        )}

        {/* Vista Performance Operatori */}
        {activeView === 'operatori' && (
          <div className="space-y-6">
            {/* Info Sistema Performance */}
            <div className="bg-gradient-to-r from-blue-50 to-slate-50 border-2 border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="text-4xl">📊</div>
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 mb-1">Riepilogo Attività per Operatore</h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="font-bold text-blue-600">Assegnate</span>
                      <span className="text-slate-700">→ Totale attività/progetti assegnati</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-bold text-green-600">Completate</span>
                      <span className="text-slate-700">→ Attività/progetti completati</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

        {/* Tabella Performance Operatori */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            📋 Attività per Operatore
          </h2>
          
          <div className="space-y-4">
            {utentiStats
              .filter(u => u.totaleProgetti > 0) // Solo utenti con attività assegnate
              .sort((a, b) => b.totaleProgetti - a.totaleProgetti) // Ordina per attività assegnate
              .slice(0, 15)
              .map((utente, index) => {
              const percentualeCompletati = utente.totaleProgetti > 0 
                ? Math.round((utente.progettiCompletati / utente.totaleProgetti) * 100)
                : 0;

              return (
                <div key={utente.id} className="relative">
                  <div className="flex items-center gap-4 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-md ${
                      index === 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' :
                      index === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-600 text-white' :
                      index === 2 ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white' :
                      'bg-slate-200 text-slate-700'
                    }`}>
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{utente.nome}</p>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          utente.ambito === 'fattibilita' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {utente.ambito === 'fattibilita' ? 'Fattibilità' : 'Ingegneria'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{utente.email}</p>
                    </div>
                    <div className="text-right flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {utente.totaleProgetti}
                        </p>
                        <p className="text-xs text-slate-500 font-medium">Assegnate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {utente.progettiCompletati}
                        </p>
                        <p className="text-xs text-slate-500 font-medium">Completate</p>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <p className={`text-lg font-bold ${getPerformanceColor(percentualeCompletati)}`}>
                          {percentualeCompletati}%
                        </p>
                        <p className="text-xs text-slate-500">Completamento</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Barra Progresso */}
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        percentualeCompletati >= 75 ? 'bg-green-500' :
                        percentualeCompletati >= 50 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${percentualeCompletati}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabella Dettaglio Operatori */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
          <div className="p-6 bg-slate-50 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Dettaglio Operatori e Progetti
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Operatore</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-blue-700 uppercase">Assegnate</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-green-700 uppercase">Completate</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 uppercase">In Corso</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-red-700 uppercase">In Ritardo</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 uppercase">Ore Std</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 uppercase">Ore Strd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {utentiStats.map(utente => (
                  <tr key={utente.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{utente.nome}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            utente.ambito === 'fattibilita' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {utente.ambito === 'fattibilita' ? 'Fattibilità' : 'Ingegneria'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{utente.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xl font-bold text-blue-600">{utente.totaleProgetti}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {utente.progettiCompletati}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {utente.progettiInCorso}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {utente.progettiInRitardo > 0 ? (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                          {utente.progettiInRitardo}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      {utente.oreStandard.toFixed(1)}h
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      {utente.oreStraordinario.toFixed(1)}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

            {/* Progetti in Ritardo */}
            {statsGenerali.progettiInRitardo > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-red-200 overflow-hidden">
                <div className="p-6 bg-red-50 border-b border-red-200">
                  <h2 className="text-xl font-bold text-red-900 flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6" />
                    Progetti in Ritardo ({statsGenerali.progettiInRitardo})
                  </h2>
                </div>

                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => {
                      if (p.stato === 'completato') return false;
                      // Se in validazione con data fine effettiva, il conteggio è sospeso
                      if (p.stato === 'in_validazione' && p.data_fine_effettiva) return false;
                      if (!p.data_fine_prevista) return false;
                      return new Date(p.data_fine_prevista) < new Date();
                    })
                    .map(progetto => {
                      const giorniRitardo = calcolaGiorniRitardo(progetto.data_fine_prevista);
                      
                      return (
                        <div key={progetto.id} className={`border-2 rounded-lg p-4 ${getRitardoColor(giorniRitardo)}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-mono font-semibold text-indigo-600">
                                  {progetto.numero_progetto}
                                </span>
                                <span className="text-slate-400">•</span>
                                <span className="font-semibold">{progetto.nome_progetto}</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-xs text-slate-600">Responsabile</p>
                                  <p className="font-medium">{progetto.ingegnere_responsabile_nome}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-600">Scadenza Prevista</p>
                                  <p className="font-medium">
                                    {new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT')}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-600">Stato</p>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    progetto.stato === 'completato' ? 'bg-green-100 text-green-700' :
                                    progetto.stato === 'in_corso' ? 'bg-blue-100 text-blue-700' :
                                    progetto.stato === 'in_validazione' ? 'bg-purple-100 text-purple-700' :
                                    progetto.stato === 'in_revisione' ? 'bg-orange-100 text-orange-700' :
                                    progetto.stato === 'sospeso' ? 'bg-red-100 text-red-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {progetto.stato === 'non_iniziato' ? 'Non Iniziato' :
                                     progetto.stato === 'in_corso' ? 'In Corso' :
                                     progetto.stato === 'in_validazione' ? 'In Validazione' :
                                     progetto.stato === 'in_revisione' ? 'In Revisione' :
                                     progetto.stato === 'completato' ? 'Completato' :
                                     progetto.stato === 'sospeso' ? 'Sospeso' :
                                     progetto.stato}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-2xl font-bold">
                                {giorniRitardo > 0 ? '+' : ''}{giorniRitardo}
                              </p>
                              <p className="text-xs">giorni</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vista Stato Progetti */}
        {activeView === 'progetti' && (
          <div className="space-y-6">
            {/* Card Non Iniziati */}
            {progetti.filter(p => p.stato === 'non_iniziato').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                <div className="p-6 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                      Non Iniziati
                    </h2>
                    <span className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'non_iniziato').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'non_iniziato')
                    .map(progetto => (
                      <div 
                        key={progetto.id} 
                        onClick={() => {
                          setProgettoDetail(progetto);
                          setModalProgettoOpen(true);
                        }}
                        className="border-2 border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-semibold text-indigo-600">
                            {progetto.numero_progetto}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cliente</p>
                            <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Responsabile</p>
                            <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Inizio</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Scadenza Prevista</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_fine_prevista ? new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Card In Corso */}
            {progetti.filter(p => p.stato === 'in_corso').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-blue-200 overflow-hidden">
                <div className="p-6 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      In Corso
                    </h2>
                    <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'in_corso').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'in_corso')
                    .map(progetto => {
                      // Se in validazione con data fine effettiva, non è in ritardo
                      const isInRitardo = progetto.data_fine_prevista && 
                        new Date(progetto.data_fine_prevista) < new Date() && 
                        !(progetto.stato === 'in_validazione' && progetto.data_fine_effettiva);
                      return (
                        <div 
                          key={progetto.id} 
                          onClick={() => {
                            setProgettoDetail(progetto);
                            setModalProgettoOpen(true);
                          }}
                          className="border-2 border-blue-200 rounded-lg p-4 hover:bg-blue-50 transition-all cursor-pointer"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono font-semibold text-indigo-600">
                              {progetto.numero_progetto}
                            </span>
                            <span className="text-slate-400">•</span>
                            <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                            {isInRitardo && (
                              <span className="ml-auto px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                In Ritardo
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-slate-500">Cliente</p>
                              <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Responsabile</p>
                              <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Data Inizio</p>
                              <p className="font-medium text-slate-700">
                                {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Scadenza Prevista</p>
                              <p className="font-medium text-slate-700">
                                {progetto.data_fine_prevista ? new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Card In Validazione */}
            {progetti.filter(p => p.stato === 'in_validazione').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-purple-200 overflow-hidden">
                <div className="p-6 bg-purple-50 border-b border-purple-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      In Validazione
                    </h2>
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'in_validazione').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'in_validazione')
                    .map(progetto => (
                      <div 
                        key={progetto.id} 
                        onClick={() => {
                          setProgettoDetail(progetto);
                          setModalProgettoOpen(true);
                        }}
                        className="border-2 border-purple-200 rounded-lg p-4 hover:bg-purple-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-semibold text-indigo-600">
                            {progetto.numero_progetto}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cliente</p>
                            <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Responsabile</p>
                            <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Inizio</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Scadenza Prevista</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_fine_prevista ? new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Card In Revisione */}
            {progetti.filter(p => p.stato === 'in_revisione').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-orange-200 overflow-hidden">
                <div className="p-6 bg-orange-50 border-b border-orange-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      In Revisione
                    </h2>
                    <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'in_revisione').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'in_revisione')
                    .map(progetto => (
                      <div 
                        key={progetto.id} 
                        onClick={() => {
                          setProgettoDetail(progetto);
                          setModalProgettoOpen(true);
                        }}
                        className="border-2 border-orange-200 rounded-lg p-4 hover:bg-orange-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-semibold text-indigo-600">
                            {progetto.numero_progetto}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cliente</p>
                            <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Responsabile</p>
                            <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Inizio</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Scadenza Prevista</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_fine_prevista ? new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Card Completati */}
            {progetti.filter(p => p.stato === 'completato').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-green-200 overflow-hidden">
                <div className="p-6 bg-green-50 border-b border-green-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-green-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      Completati
                    </h2>
                    <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'completato').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'completato')
                    .map(progetto => (
                      <div 
                        key={progetto.id} 
                        onClick={() => {
                          setProgettoDetail(progetto);
                          setModalProgettoOpen(true);
                        }}
                        className="border-2 border-green-200 rounded-lg p-4 hover:bg-green-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-semibold text-indigo-600">
                            {progetto.numero_progetto}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cliente</p>
                            <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Responsabile</p>
                            <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Inizio</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Completamento</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_completamento ? new Date(progetto.data_completamento).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Card Sospesi */}
            {progetti.filter(p => p.stato === 'sospeso').length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-red-200 overflow-hidden">
                <div className="p-6 bg-red-50 border-b border-red-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-red-900 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      Sospesi
                    </h2>
                    <span className="px-4 py-2 bg-red-100 text-red-700 rounded-full font-bold text-lg">
                      {progetti.filter(p => p.stato === 'sospeso').length}
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {progetti
                    .filter(p => p.stato === 'sospeso')
                    .map(progetto => (
                      <div 
                        key={progetto.id} 
                        onClick={() => {
                          setProgettoDetail(progetto);
                          setModalProgettoOpen(true);
                        }}
                        className="border-2 border-red-200 rounded-lg p-4 hover:bg-red-50 transition-all cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono font-semibold text-indigo-600">
                            {progetto.numero_progetto}
                          </span>
                          <span className="text-slate-400">•</span>
                          <span className="font-semibold text-slate-900">{progetto.nome_progetto}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cliente</p>
                            <p className="font-medium text-slate-700">{progetto.cliente_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Responsabile</p>
                            <p className="font-medium text-slate-700">{progetto.ingegnere_responsabile_nome || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Data Inizio</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_inizio ? new Date(progetto.data_inizio).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Scadenza Prevista</p>
                            <p className="font-medium text-slate-700">
                              {progetto.data_fine_prevista ? new Date(progetto.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {progetti.length === 0 && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 p-12 text-center">
                <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg text-slate-600">Nessun progetto trovato nel periodo selezionato</p>
              </div>
            )}
          </div>
        )}

        {/* Vista Fatturato */}
        {activeView === 'fatturato' && (
          <div className="space-y-6">
            {/* Card Mensile */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="p-6 bg-blue-50 border-b border-blue-200">
                <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  Fatturato Mensile (Ultimo Mese)
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Fatturato Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'fatturato',
                        periodo: 'Mensile',
                        ordini: statsFatturato.mensile.ordiniFatturati
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-green-700 font-semibold mb-2">💰 Fatturato Totale</p>
                    <p className="text-4xl font-bold text-green-600">
                      €{statsFatturato.mensile.fatturato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-green-600 mt-2">{statsFatturato.mensile.ordiniFatturati.length} ordini</p>
                  </div>

                  {/* Da Fatturare Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'da_fatturare',
                        periodo: 'Mensile',
                        ordini: statsFatturato.mensile.ordiniDaFatturare
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-orange-700 font-semibold mb-2">📋 Da Fatturare</p>
                    <p className="text-4xl font-bold text-orange-600">
                      €{statsFatturato.mensile.daFatturare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-orange-600 mt-2">{statsFatturato.mensile.ordiniDaFatturare.length} ordini</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Trimestrale */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="p-6 bg-purple-50 border-b border-purple-200">
                <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  Fatturato Trimestrale (Ultimi 3 Mesi)
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Fatturato Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'fatturato',
                        periodo: 'Trimestrale',
                        ordini: statsFatturato.trimestrale.ordiniFatturati
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-green-700 font-semibold mb-2">💰 Fatturato Totale</p>
                    <p className="text-4xl font-bold text-green-600">
                      €{statsFatturato.trimestrale.fatturato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-green-600 mt-2">{statsFatturato.trimestrale.ordiniFatturati.length} ordini</p>
                  </div>

                  {/* Da Fatturare Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'da_fatturare',
                        periodo: 'Trimestrale',
                        ordini: statsFatturato.trimestrale.ordiniDaFatturare
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-orange-700 font-semibold mb-2">📋 Da Fatturare</p>
                    <p className="text-4xl font-bold text-orange-600">
                      €{statsFatturato.trimestrale.daFatturare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-orange-600 mt-2">{statsFatturato.trimestrale.ordiniDaFatturare.length} ordini</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Annuale */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="p-6 bg-indigo-50 border-b border-indigo-200">
                <h2 className="text-xl font-bold text-indigo-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  Fatturato Annuale (Ultimo Anno)
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Fatturato Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'fatturato',
                        periodo: 'Annuale',
                        ordini: statsFatturato.annuale.ordiniFatturati
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-green-700 font-semibold mb-2">💰 Fatturato Totale</p>
                    <p className="text-4xl font-bold text-green-600">
                      €{statsFatturato.annuale.fatturato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-green-600 mt-2">{statsFatturato.annuale.ordiniFatturati.length} ordini</p>
                  </div>

                  {/* Da Fatturare Card */}
                  <div 
                    onClick={() => {
                      setModalFatturatoData({
                        tipo: 'da_fatturare',
                        periodo: 'Annuale',
                        ordini: statsFatturato.annuale.ordiniDaFatturare
                      });
                      setModalFatturatoOpen(true);
                    }}
                    className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                  >
                    <p className="text-sm text-orange-700 font-semibold mb-2">📋 Da Fatturare</p>
                    <p className="text-4xl font-bold text-orange-600">
                      €{statsFatturato.annuale.daFatturare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-orange-600 mt-2">{statsFatturato.annuale.ordiniDaFatturare.length} ordini</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vista Cantieri */}
        {activeView === 'cantieri' && (
          <div className="space-y-6">
            {/* Stats Cantieri */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
                <p className="text-sm text-slate-600">Totale Cantieri</p>
                <p className="text-3xl font-bold text-yellow-600">{cantieri.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
                <p className="text-sm text-slate-600">Attivi</p>
                <p className="text-3xl font-bold text-blue-600">
                  {cantieri.filter(c => c.stato === 'attivo').length}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
                <p className="text-sm text-slate-600">Completati</p>
                <p className="text-3xl font-bold text-green-600">
                  {cantieri.filter(c => c.stato === 'completato').length}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
                <p className="text-sm text-slate-600">Sopralluoghi Totali</p>
                <p className="text-3xl font-bold text-orange-600">
                  {cantieri.reduce((sum, c) => sum + (c.sopralluoghi?.length || 0), 0)}
                </p>
              </div>
            </div>

            {/* Tabella Cantieri con Sopralluoghi */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-yellow-500 to-amber-500">
                <h2 className="text-xl font-bold text-white">Riepilogo Cantieri e Sopralluoghi</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Cantiere</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Stato</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Sopralluoghi</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Dettaglio Sopralluoghi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {cantieri.map((cantiere) => {
                      const sopralluoghi = cantiere.sopralluoghi || [];
                      const sopralluoghiPerTipo = sopralluoghi.reduce((acc, s) => {
                        acc[s.tipo_attivita] = (acc[s.tipo_attivita] || 0) + 1;
                        return acc;
                      }, {});
                      
                      return (
                        <tr key={cantiere.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-mono font-semibold text-indigo-600 text-sm">
                                {cantiere.codice_progetto_cliente}
                              </p>
                              <p className="text-sm text-slate-900">{cantiere.nome_progetto}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              cantiere.stato === 'attivo' ? 'bg-blue-100 text-blue-700' :
                              cantiere.stato === 'completato' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {cantiere.stato.charAt(0).toUpperCase() + cantiere.stato.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-sm">
                            <div className="text-slate-600">
                              <p>{new Date(cantiere.data_inizio).toLocaleDateString('it-IT')}</p>
                              <p className="text-xs">→</p>
                              <p>{new Date(cantiere.data_fine).toLocaleDateString('it-IT')}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 text-orange-700 font-bold">
                              {sopralluoghi.length}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {Object.entries(sopralluoghiPerTipo).map(([tipo, count]) => (
                                <span key={tipo} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  {tipo}: {count}
                                </span>
                              ))}
                              {sopralluoghi.length === 0 && (
                                <span className="text-xs text-slate-400">Nessun sopralluogo</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Modal Fatturato */}
        {modalFatturatoOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">
                    {modalFatturatoData.tipo === 'fatturato' ? '💰 Ordini Fatturati' : '📋 Ordini Da Fatturare'}
                  </h2>
                  <p className="text-sm opacity-90">Periodo: {modalFatturatoData.periodo}</p>
                </div>
                <button
                  onClick={() => setModalFatturatoOpen(false)}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                {modalFatturatoData.ordini.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-lg text-slate-600">Nessun ordine trovato</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {modalFatturatoData.ordini.map(ordine => (
                      <div key={ordine.id} className="border-2 border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-semibold text-indigo-600">{ordine.numero_ordine}</span>
                              <span className="text-slate-400">•</span>
                              <span className="font-semibold text-slate-900">{ordine.cliente_nome}</span>
                            </div>
                            <p className="text-sm text-slate-600">{ordine.descrizione}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-2xl font-bold text-green-600">
                              €{(modalFatturatoData.tipo === 'fatturato' ? ordine.importoFatturato : ordine.importoDaFatturare).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>

                        {/* Lista SAL */}
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-700 mb-2">
                            SAL {modalFatturatoData.tipo === 'fatturato' ? 'Fatturati' : 'Da Fatturare'}:
                          </p>
                          <div className="space-y-2">
                            {(modalFatturatoData.tipo === 'fatturato' ? ordine.salFatturati : ordine.salDaFatturare).map((sal, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 rounded p-2">
                                <span className="text-slate-700">{sal.descrizione}</span>
                                <span className="font-semibold text-slate-900">
                                  €{sal.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Dettaglio Progetto */}
        {modalProgettoOpen && progettoDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-500 text-white flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{progettoDetail.numero_progetto}</h2>
                  <p className="text-sm opacity-90">{progettoDetail.nome_progetto}</p>
                </div>
                <button
                  onClick={() => {
                    setModalProgettoOpen(false);
                    setProgettoDetail(null);
                  }}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {/* Stato */}
                <div>
                  <p className="text-xs text-slate-500 mb-1">Stato</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    progettoDetail.stato === 'completato' ? 'bg-green-100 text-green-700' :
                    progettoDetail.stato === 'in_corso' ? 'bg-blue-100 text-blue-700' :
                    progettoDetail.stato === 'in_validazione' ? 'bg-purple-100 text-purple-700' :
                    progettoDetail.stato === 'in_revisione' ? 'bg-orange-100 text-orange-700' :
                    progettoDetail.stato === 'sospeso' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {progettoDetail.stato === 'non_iniziato' ? 'Non Iniziato' :
                     progettoDetail.stato === 'in_corso' ? 'In Corso' :
                     progettoDetail.stato === 'in_validazione' ? 'In Validazione' :
                     progettoDetail.stato === 'in_revisione' ? 'In Revisione' :
                     progettoDetail.stato === 'completato' ? 'Completato' :
                     progettoDetail.stato === 'sospeso' ? 'Sospeso' :
                     progettoDetail.stato}
                  </span>
                </div>

                {/* Informazioni Base */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Cliente</p>
                    <p className="font-medium text-slate-900">{progettoDetail.cliente_nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Ordine di Lavoro</p>
                    <p className="font-medium text-indigo-600">{progettoDetail.ordine_lavoro_numero || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Responsabile</p>
                    <p className="font-medium text-slate-900">{progettoDetail.ingegnere_responsabile_nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Data Inizio</p>
                    <p className="font-medium text-slate-900">
                      {progettoDetail.data_inizio ? new Date(progettoDetail.data_inizio).toLocaleDateString('it-IT') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Scadenza Prevista</p>
                    <p className="font-medium text-slate-900">
                      {progettoDetail.data_fine_prevista ? new Date(progettoDetail.data_fine_prevista).toLocaleDateString('it-IT') : '-'}
                    </p>
                  </div>
                  {progettoDetail.data_completamento && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Data Completamento</p>
                      <p className="font-medium text-slate-900">
                        {new Date(progettoDetail.data_completamento).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Ore Lavorate */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Ore Lavorate</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Ore Standard</p>
                      <p className="text-lg font-bold text-blue-600">
                        {progettoDetail.ore_lavorate_standard || 0}h
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Ore Straordinario</p>
                      <p className="text-lg font-bold text-orange-600">
                        {progettoDetail.ore_straordinario || 0}h
                      </p>
                    </div>
                  </div>
                </div>

                {/* Revisioni */}
                {progettoDetail.revisioni && progettoDetail.revisioni.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">
                      Revisioni ({progettoDetail.revisioni.length})
                    </p>
                    <div className="space-y-2">
                      {progettoDetail.revisioni.map(revisione => (
                        <div key={revisione.id} className="bg-slate-50 rounded p-3 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-slate-700">Revisione #{revisione.numero}</span>
                            <span className="text-slate-600">{revisione.ore}h</span>
                          </div>
                          <p className="text-xs text-slate-500">{revisione.descrizione}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(revisione.data).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Descrizione e Note */}
                {progettoDetail.descrizione && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Descrizione</p>
                    <p className="text-sm text-slate-700">{progettoDetail.descrizione}</p>
                  </div>
                )}
                {progettoDetail.note && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Note</p>
                    <p className="text-sm text-slate-700">{progettoDetail.note}</p>
                  </div>
                )}

                {/* Pulsante Vai all'Ordine */}
                {progettoDetail.ordine_lavoro_id && (
                  <Button
                    onClick={() => {
                      navigate(`/ordini-lavoro?ordine=${progettoDetail.ordine_lavoro_id}`);
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Vai all&apos;Ordine di Lavoro
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
